import React, { useState, useContext, createContext } from 'react'
import { reset as resetUserStore } from '../../features/user/userSlice'
import { reset as resetGlobalStore } from '../../features/globals/globalSlice'
import useAxios from './useAxios'
import {
  addUser,
  addBusiness,
  addBuyer,
  addSupplier,
  addLocations,
  loggedIn,
} from '../../features/user/userSlice'
import { useDispatch } from 'react-redux'
import { useInterval } from '../../hooks/useInterval'

const authContext = createContext()
// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }) {
  const auth = useProvideAuth()
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext)
}

// Provider hook that creates auth object and handles state
const useProvideAuth = () => {
  const dispatch = useDispatch()
  const axios = useAxios()
  const [user, setUser] = useState(null)
  const [error, setError] = useState(false)
  const [lastVerifyOn, setLastVerifyOn] = useState()
  // Track if a token refresh is in progress to prevent duplicate refreshes
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Store pending requests that are waiting for token refresh
  const [pendingRequests, setPendingRequests] = useState([])

  // Utility function to check if a token is expired
  const isTokenExpired = (token) => {
    if (!token) return true

    try {
      // Parse JWT payload
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const payload = JSON.parse(window.atob(base64))

      // Check if token is expired
      const currentTime = Math.floor(Date.now() / 1000)
      return payload.exp < currentTime
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true
    }
  }

  // Function to refresh the token using refresh token
  const refreshToken = async () => {
    // If already refreshing, don't start another refresh
    if (isRefreshing) {
      console.log('Token refresh already in progress, waiting...')

      // Return a promise that resolves when the current refresh completes
      return new Promise((resolve) => {
        setPendingRequests((prev) => [...prev, resolve])
      })
    }

    try {
      setIsRefreshing(true)
      const refresh = localStorage.getItem('refresh_token')
      if (!refresh) {
        setIsRefreshing(false)
        // Resolve any pending requests with failure
        pendingRequests.forEach((resolve) => resolve(false))
        setPendingRequests([])
        return false
      }

      // Create a clean axios instance to avoid interceptors
      const refreshAxios = axios.create({
        headers: {
          'Content-Type': 'application/json',
        },
      })

      console.log('Attempting to refresh token')
      const response = await refreshAxios.post('main/token/refresh/', {
        refresh: refresh,
      })

      let success = false
      if (response && response.data) {
        console.log('Token refresh successful')

        if (response.data.access) {
          localStorage.setItem('access_token', response.data.access)
          success = true
        }

        if (response.data.refresh) {
          console.log('New refresh token received')
          localStorage.setItem('refresh_token', response.data.refresh)
        }
      } else {
        console.log('Token refresh response did not contain new tokens')
      }

      // Resolve all pending requests
      pendingRequests.forEach((resolve) => resolve(success))
      setPendingRequests([])

      return success
    } catch (error) {
      console.error('Failed to refresh token:', error)

      if (error.response && error.response.status === 401) {
        console.error('Refresh token is invalid or expired')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.setItem('is_logged_in', false)
        setUser(null)
      }

      // Resolve all pending requests with failure
      pendingRequests.forEach((resolve) => resolve(false))
      setPendingRequests([])

      return false
    } finally {
      setIsRefreshing(false)
    }
  }

  const signin = async (username, password) => {
    try {
      setError(false)
      setUser(null)

      const response = await axios.post('main/token/obtain/', {
        email: username,
        password: password,
      })
      console.log('RESPONSE', response)
      console.log('Access Token', response.data.access)
      console.log('Refresh Token', response.data.refresh)
      localStorage.setItem('access_token', response.data.access)
      localStorage.setItem('refresh_token', response.data.refresh)
      localStorage.setItem('is_logged_in', true)
      return response
    } catch (error) {
      console.error('Sign in error:', error)
      setError(true)
      return false
    }
  }

  const signout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token')

      // Clear local storage first to prevent further requests with invalid tokens
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.setItem('is_logged_in', false)

      // Reset Redux state
      dispatch(resetUserStore())
      dispatch(resetGlobalStore())

      // Only attempt to blacklist if we have a token
      if (refreshToken) {
        try {
          await axios.post('/main/blacklist/', {
            refresh_token: refreshToken,
          })
        } catch (error) {
          console.error('Error blacklisting token:', error)
          // Continue anyway since we've already cleared local storage
        }
      }

      // Reset user state
      setUser(null)
    } catch (error) {
      console.error('Error during signout:', error)
    }
  }

  const loggedInVerify = async () => {
    try {
      const accessToken = localStorage.getItem('access_token')

      // If token is expired, try refreshing before verification
      if (accessToken && isTokenExpired(accessToken)) {
        const refreshSuccessful = await refreshToken()
        if (!refreshSuccessful) {
          setUser(null)
          return false
        }
      }

      const response = await axios.post('main/token/verify/')
      if (response && response.data && response.data.success) {
        // Update tokens from response
        localStorage.setItem('access_token', response.data.access)
        localStorage.setItem('refresh_token', response.data.refresh)

        // Update user data in state and Redux
        if (response?.data?.user) {
          setUser(response?.data?.user)
          dispatch(loggedIn())
          localStorage.setItem('is_logged_in', true)
          dispatch(addUser(response.data.user))

          // Add business related data if available
          if (response.data.user?.business) {
            dispatch(addBusiness(response.data.user.business))
          }
          if (response.data.user?.business?.locations) {
            dispatch(addLocations(response.data.user?.business?.locations))
          }
          if (response.data.user?.business?.buyer) {
            dispatch(addBuyer(response.data.user?.business?.buyer))
          }
          if (response.data.user?.business?.supplier) {
            dispatch(addSupplier(response.data.user?.business?.supplier))
          }
        }
        return response
      } else {
        setUser(null)
        localStorage.setItem('is_logged_in', false)
        return false
      }
    } catch (error) {
      console.error('Verification error:', error)

      // Try to refresh token if verification fails
      try {
        const refreshSuccessful = await refreshToken()
        if (!refreshSuccessful) {
          setUser(null)
          localStorage.setItem('is_logged_in', false)
          return false
        }

        // Try verification again with the new token
        try {
          const response = await axios.post('main/token/verify/')
          if (response && response.data && response.data.success) {
            // Update user data with the new response
            setUser(response?.data?.user)
            dispatch(loggedIn())
            localStorage.setItem('is_logged_in', true)
            return response
          } else {
            setUser(null)
            localStorage.setItem('is_logged_in', false)
            return false
          }
        } catch (secondError) {
          console.error('Second verification error:', secondError)
          setUser(null)
          localStorage.setItem('is_logged_in', false)
          return false
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError)
        setUser(null)
        localStorage.setItem('is_logged_in', false)
        return false
      }
    }
  }

  const isLoggedIn = async () => {
    console.log('checking token status')

    // Check if we have tokens at all
    const accessToken = localStorage.getItem('access_token')
    const refreshTokenStr = localStorage.getItem('refresh_token')

    if (!accessToken || !refreshTokenStr) {
      console.log('No tokens found')
      setUser(null)
      localStorage.setItem('is_logged_in', false)
      return false
    }

    // Always update last verify time to prevent excessive checks
    setLastVerifyOn(new Date())

    // Check if access token is expired and refresh if needed
    if (isTokenExpired(accessToken)) {
      console.log('Access token expired, attempting refresh')
      const refreshSuccessful = await refreshToken()
      if (!refreshSuccessful) {
        console.log('Refresh failed, but not logging out immediately')
        // Don't log out immediately - the token might still be valid on server
        // Let's verify with the server before logging out
      }
    }

    // Verify the token with the server
    try {
      const response = await axios.post('main/token/verify/')
      console.log('Token verification successful')

      // Update tokens from verification if available
      if (response && response.data) {
        if (response.data.access) {
          localStorage.setItem('access_token', response.data.access)
        }
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh)
        }

        // Update user data if available
        if (response.data.user) {
          setUser(response.data.user)
          dispatch(loggedIn())
          localStorage.setItem('is_logged_in', true)

          // Update other user data in Redux if available
          dispatch(addUser(response.data.user))
          if (response.data.user?.business) {
            dispatch(addBusiness(response.data.user.business))
          }
          if (response.data.user?.business?.locations) {
            dispatch(addLocations(response.data.user?.business?.locations))
          }
          if (response.data.user?.business?.buyer) {
            dispatch(addBuyer(response.data.user?.business?.buyer))
          }
          if (response.data.user?.business?.supplier) {
            dispatch(addSupplier(response.data.user?.business?.supplier))
          }
        }
      }

      return true
    } catch (error) {
      console.log('Verification failed, attempting refresh one more time')

      // One more refresh attempt
      const refreshSuccessful = await refreshToken()
      if (!refreshSuccessful) {
        console.log(
          'Both verification and refresh failed, user needs to log in again'
        )

        // Now we can be more confident the session is truly expired
        await signout()
        return false
      }

      // Try verification one more time with new token
      try {
        const response = await axios.post('main/token/verify/')
        console.log('Second verification attempt successful')
        return true
      } catch (secondError) {
        console.log('All authentication attempts failed, logging out')
        await signout()
        return false
      }
    }
  }

  // Proactively refresh the token if it's about to expire
  const checkAndRefreshToken = () => {
    try {
      const accessToken = localStorage.getItem('access_token')
      if (!accessToken) return

      // Get token expiration time
      const tokenData = accessToken.split('.')[1]
      const decodedToken = JSON.parse(atob(tokenData))
      const expTime = decodedToken.exp * 1000 // Convert to milliseconds
      const currentTime = Date.now()
      const timeRemaining = expTime - currentTime

      // If token will expire in the next 5 minutes, refresh it
      if (timeRemaining < 5 * 60 * 1000 && timeRemaining > 0) {
        console.log(
          `Token expires in ${Math.round(
            timeRemaining / 1000
          )} seconds, refreshing proactively`
        )
        refreshToken()
      }
    } catch (error) {
      console.error(
        'Error checking token expiration for proactive refresh:',
        error
      )
    }
  }

  // Periodically check and refresh token
  useInterval(checkAndRefreshToken, 60 * 1000) // Check every minute

  // Configure axios interceptors for token handling
  React.useEffect(() => {
    let refreshPromise = null

    // Request interceptor to add tokens
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        // Skip interceptor for refresh token requests to avoid loops
        const isAuthEndpoint =
          config.url &&
          (config.url.includes('token/obtain') ||
            config.url.includes('token/refresh') ||
            config.url.includes('token/verify') ||
            config.url.includes('blacklist'))

        if (isAuthEndpoint) {
          return config
        }

        // Get current access token
        const accessToken = localStorage.getItem('access_token')
        if (!accessToken) {
          return config
        }

        // Check if token is expired
        if (isTokenExpired(accessToken)) {
          // Use singleton pattern for refresh - all requests use same promise
          if (!refreshPromise) {
            console.log('Creating new refresh promise')
            refreshPromise = refreshToken().finally(() => {
              refreshPromise = null // Clear when done
            })
          } else {
            console.log('Using existing refresh promise')
          }

          // Wait for the refresh to complete
          const refreshSuccessful = await refreshPromise

          // Get new token after refresh
          const newToken = localStorage.getItem('access_token')
          if (refreshSuccessful && newToken) {
            config.headers.Authorization = `Bearer ${newToken}`
          }
        } else {
          // Token not expired, use it
          config.headers.Authorization = `Bearer ${accessToken}`
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor to handle 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {}

        // Skip for auth endpoints and already retried requests
        const isAuthEndpoint =
          originalRequest.url &&
          (originalRequest.url.includes('token/obtain') ||
            originalRequest.url.includes('token/refresh') ||
            originalRequest.url.includes('token/verify') ||
            originalRequest.url.includes('blacklist'))

        if (
          !error.response ||
          error.response.status !== 401 ||
          isAuthEndpoint ||
          originalRequest._retry
        ) {
          return Promise.reject(error)
        }

        // Mark as retried to prevent infinite loops
        originalRequest._retry = true

        // Use singleton pattern for refresh
        if (!refreshPromise) {
          console.log('Creating new refresh promise (from 401 handler)')
          refreshPromise = refreshToken().finally(() => {
            refreshPromise = null
          })
        } else {
          console.log('Using existing refresh promise (from 401 handler)')
        }

        try {
          const refreshSuccessful = await refreshPromise

          if (refreshSuccessful) {
            const newToken = localStorage.getItem('access_token')
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return axios(originalRequest)
          }
        } catch (refreshError) {
          console.error('Refresh failed during 401 handling:', refreshError)
        }

        // Check if this is a critical route that requires authentication
        const requiresAuth =
          originalRequest.url &&
          (originalRequest.url.includes('/orders/') ||
            originalRequest.url.includes('/profile/') ||
            originalRequest.url.includes('/account/'))

        if (requiresAuth) {
          console.log(
            'Critical authenticated endpoint failed, redirecting to login'
          )
          await signout()
          window.location.href = '/login'
        }

        return Promise.reject(error)
      }
    )

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor)
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, []) // This syntax for empty dependency array should work

  return {
    user,
    setUser,
    signin,
    signout,
    error,
    isLoggedIn,
    loggedInVerify,
    refreshToken, // Exposing this might be useful in some cases
    isTokenExpired, // Exposing this might be useful in some cases
  }
}
